import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  Stack
} from "@mui/material";
import { RootState, useAppDispatch } from "../../store";
import { Agent, AppointmentType, CompleteTemplatePlanOfCare, CompleteTemplatePlanOfCareWeek, TemplatePlanOfCareAppointment } from "../../types";
import { filterAppointmentTypesForPicker } from "../../utils/appointmentTypes";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { Colors } from "../../Colors";
import FunctionalModulesLayout from "./FunctionalModulesLayout";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import SettingRow from "./SettingRow";
import { NumberTextField } from "../../components/text-fields/NumberTextField";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

export default function InitialVisit() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = agents && agents.length > 0 ? agents[0] : null;

  const [initialVisitBookingLeadTime, setInitialVisitBookingLeadTime] = useState<number>(24);
  const [autoAddPatientsAfterIE, setAutoAddPatientsAfterIE] = useState<boolean>(false);
  const [autoAssignPOCAfterIE, setAutoAssignPOCAfterIE] = useState<boolean>(false);
  const [autoSendPOCLinkAfterIE, setAutoSendPOCLinkAfterIE] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingAppointmentTypes, setLoadingAppointmentTypes] = useState<boolean>(false);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState<boolean>(false);

  const [templatePlanOfCareId, setTemplatePlanOfCareId] = useState<string>("");
  const [numberOfWeeks, setNumberOfWeeks] = useState<number>(4);
  const [visitsPerWeek, setVisitsPerWeek] = useState<number>(2);
  const [gap, setGap] = useState<number>(0);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [firstVisit, setFirstVisit] = useState<AppointmentType | null>(null);
  const [mostVisits, setMostVisits] = useState<AppointmentType | null>(null);
  const weekVisitOptions = [1, 2, 3, 4, 5];
  const gapOptions = [0, 1, 2, 3];

  useEffect(() => {
    if (agent) {
      setInitialVisitBookingLeadTime(agent.initialVisitBookingLeadTime || 24);
      setAutoAddPatientsAfterIE(agent.autoAddPatientsAfterIE || false);
      setAutoAssignPOCAfterIE(agent.autoAssignPOCAfterIE || false);
      setAutoSendPOCLinkAfterIE(agent.autoSendPOCLinkAfterIE || false);
      setTemplatePlanOfCareId(agent.templatePlanOfCareId || "");
    } else {
      setInitialVisitBookingLeadTime(24);
      setAutoAddPatientsAfterIE(false);
      setAutoAssignPOCAfterIE(false);
      setAutoSendPOCLinkAfterIE(false);
      setTemplatePlanOfCareId("");
    }
  }, [agent]);

  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      if (!user?.token) {
        return;
      }

      setLoadingAppointmentTypes(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        });

        const sortedAppointmentTypes = response.data.sort((a: AppointmentType, b: AppointmentType) => a.name.localeCompare(b.name)) as AppointmentType[];
        setAppointmentTypes(sortedAppointmentTypes);

        const defaultFollowUp = sortedAppointmentTypes.find((type) => type.name === "Follow-Up");
        const defaultInitialExam = sortedAppointmentTypes.find((type) => type.name === "Initial Examination");
        setMostVisits((prev) => prev || defaultFollowUp || sortedAppointmentTypes[0] || null);
        setFirstVisit((prev) => prev || defaultInitialExam || sortedAppointmentTypes[0] || null);
      } catch (error) {
        console.error("Failed to fetch appointment types:", error);
        showMessage("Failed to load appointment types.", "error");
      } finally {
        setLoadingAppointmentTypes(false);
      }
    };

    fetchAppointmentTypes();
  }, [showMessage, user?.token]);

  useEffect(() => {
    const fetchTemplateDetails = async () => {
      if (!user?.token || !templatePlanOfCareId || appointmentTypes.length === 0) {
        return;
      }

      setLoadingTemplateDetails(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/templates`, {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        });
        const templates = response.data as CompleteTemplatePlanOfCare[];
        const selectedTemplate = templates.find((template) => template.templatePlanOfCareId === templatePlanOfCareId);

        if (!selectedTemplate) {
          return;
        }

        const weeks = selectedTemplate.templatePlanOfCareWeeks || [];
        if (weeks.length > 0) {
          setNumberOfWeeks(weeks.length);
          const preferredWeek = weeks.find((week) => week.templatePlanOfCareAppointments.length > 0) || weeks[0];
          setVisitsPerWeek(Math.max(1, preferredWeek.templatePlanOfCareAppointments.length || 1));
        }
        setGap(selectedTemplate.gap || 0);

        const allAppointments = weeks.flatMap((week) => week.templatePlanOfCareAppointments || []);
        const firstAppointment = allAppointments[0];
        if (firstAppointment) {
          const firstType = appointmentTypes.find((type) => type.appointmentTypeId === firstAppointment.appointmentTypeId) || null;
          setFirstVisit(firstType);
        }

        const nonFirstAppointments = allAppointments.slice(1);
        const counts: Record<string, number> = {};
        nonFirstAppointments.forEach((appointment) => {
          counts[appointment.appointmentTypeId] = (counts[appointment.appointmentTypeId] || 0) + 1;
        });
        const mostFrequentTypeId = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
        const fallbackTypeId = allAppointments[allAppointments.length - 1]?.appointmentTypeId;
        const defaultTypeId = mostFrequentTypeId || fallbackTypeId;
        if (defaultTypeId) {
          const defaultType = appointmentTypes.find((type) => type.appointmentTypeId === defaultTypeId) || null;
          setMostVisits(defaultType);
        }
      } catch (error) {
        console.error("Failed to load template details:", error);
        showMessage("Failed to load template details.", "error");
      } finally {
        setLoadingTemplateDetails(false);
      }
    };

    fetchTemplateDetails();
  }, [appointmentTypes, showMessage, templatePlanOfCareId, user?.token]);

  const previewPlanRows = useMemo(() => {
    const rows: { weekNumber: number; visits: { visitNumber: number; appointmentTypeName: string }[] }[] = [];
    const fallbackType = mostVisits?.name || firstVisit?.name || "Unassigned";
    let visitNumber = 1;

    for (let weekIdx = 0; weekIdx < numberOfWeeks; weekIdx++) {
      const weekVisits: { visitNumber: number; appointmentTypeName: string }[] = [];
      for (let visitIdx = 0; visitIdx < visitsPerWeek; visitIdx++) {
        weekVisits.push({
          visitNumber,
          appointmentTypeName: visitNumber === 1 ? firstVisit?.name || fallbackType : mostVisits?.name || fallbackType
        });
        visitNumber++;
      }
      rows.push({ weekNumber: weekIdx + 1, visits: weekVisits });
    }

    return rows;
  }, [firstVisit?.name, mostVisits?.name, numberOfWeeks, visitsPerWeek]);

  const generateTemplatePlanOfCare = useCallback((existingTemplateId?: string): CompleteTemplatePlanOfCare | null => {
    if (!user?.superOrganizationId || !user.organizationId || !firstVisit || !mostVisits || numberOfWeeks <= 0 || visitsPerWeek <= 0) {
      return null;
    }

    const planOfCareId = existingTemplateId || uuidv4();
    const template: CompleteTemplatePlanOfCare = {
      superOrganizationId: user.superOrganizationId,
      organizationId: user.organizationId,
      templatePlanOfCareId: planOfCareId,
      name: "Initial Visit Plan of Care",
      numAppointments: numberOfWeeks * visitsPerWeek,
      gap,
      templatePlanOfCareWeeks: [],
      templatePlanOfCareWeekIds: []
    };

    let isFirstVisit = true;

    for (let i = 0; i < numberOfWeeks; i++) {
      const weekId = uuidv4();
      const week: CompleteTemplatePlanOfCareWeek = {
        superOrganizationId: user.superOrganizationId,
        organizationId: user.organizationId,
        templatePlanOfCareId: planOfCareId,
        templatePlanOfCareWeekId: weekId,
        templatePlanOfCareAppointments: [],
        templatePlanOfCareAppointmentIds: []
      };

      for (let j = 0; j < visitsPerWeek; j++) {
        const appointmentTypeId = isFirstVisit ? firstVisit.appointmentTypeId : mostVisits.appointmentTypeId;
        isFirstVisit = false;
        const appointmentId = uuidv4();
        const appointment: TemplatePlanOfCareAppointment = {
          superOrganizationId: user.superOrganizationId,
          organizationId: user.organizationId,
          templatePlanOfCareId: planOfCareId,
          templatePlanOfCareWeekId: weekId,
          templatePlanOfCareAppointmentId: appointmentId,
          appointmentTypeId
        };
        week.templatePlanOfCareAppointments.push(appointment);
        week.templatePlanOfCareAppointmentIds.push(appointmentId);
      }

      template.templatePlanOfCareWeeks.push(week);
      template.templatePlanOfCareWeekIds.push(weekId);
    }

    return template;
  }, [firstVisit, gap, mostVisits, numberOfWeeks, user?.organizationId, user?.superOrganizationId, visitsPerWeek]);

  const handleSave = useCallback(async () => {
    if (!agent || !user?.token) {
      showMessage("Agent data or user session not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      let nextTemplatePlanOfCareId = templatePlanOfCareId;

      if (autoAssignPOCAfterIE) {
        const template = generateTemplatePlanOfCare(templatePlanOfCareId || undefined);
        if (!template) {
          showMessage("Please complete template settings before saving.", "error");
          setIsLoading(false);
          return;
        }

        if (templatePlanOfCareId) {
          await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/templates/${templatePlanOfCareId}`, template, {
            headers: {
              Authorization: `Bearer ${user.token}`
            }
          });
        } else {
          const createResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/templates`, template, {
            headers: {
              Authorization: `Bearer ${user.token}`
            }
          });
          nextTemplatePlanOfCareId = createResponse.data?.templatePlanOfCareId || template.templatePlanOfCareId;
          setTemplatePlanOfCareId(nextTemplatePlanOfCareId);
        }
      }

      const agentProps: Partial<Agent> = {
        agentId: agent.agentId,
        initialVisitBookingLeadTime: initialVisitBookingLeadTime,
        autoAddPatientsAfterIE: autoAddPatientsAfterIE,
        autoAssignPOCAfterIE: autoAssignPOCAfterIE,
        autoSendPOCLinkAfterIE: autoSendPOCLinkAfterIE,
        templatePlanOfCareId: nextTemplatePlanOfCareId || undefined
      };
      await dispatch(
        updateAgentThunk({ token: user.token, agent: agentProps })
      ).unwrap();
      showMessage("Initial visit settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update initial visit settings:", error);
      showMessage("Failed to update initial visit settings.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [
    agent,
    user?.token,
    initialVisitBookingLeadTime,
    autoAddPatientsAfterIE,
    autoAssignPOCAfterIE,
    autoSendPOCLinkAfterIE,
    templatePlanOfCareId,
    generateTemplatePlanOfCare,
    dispatch,
    showMessage
  ]);

  if (!agent) {
    return (
      <FunctionalModulesLayout>
        <CircularProgress size={30} />
      </FunctionalModulesLayout>
    );
  }

  return (
    <FunctionalModulesLayout>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Initial Visit
      </Typography>
      <Typography variant="h6" sx={{ mt: 1 }}>
        Booking Settings
      </Typography>
      <SettingRow>
        <Typography
          variant="body1"
          display="flex"
          alignItems="center"
          gap={1}
        >
          Don&apos;t allow bookings within
          <NumberTextField
            value={initialVisitBookingLeadTime}
            onChange={(value) => setInitialVisitBookingLeadTime(value)}
          />
          hours of the appointment start time
        </Typography>
      </SettingRow>
      <Typography variant="h6" sx={{ mt: 4 }}>
        Automation Settings
      </Typography>
      <SettingRow label="Automatically add patients to Penciled during an Initial Examination" description="Recommended">
        <Checkbox
          checked={autoAddPatientsAfterIE}
          onChange={() => setAutoAddPatientsAfterIE(!autoAddPatientsAfterIE)}
        />
      </SettingRow>
      <SettingRow
        label="Automatically assign a Plan of Care to patients during an Initial Examination"
        description="Recommended"
        openCollapse={autoAssignPOCAfterIE}
        collapseChildren={
          <Box>
            <Typography variant="body2" sx={{ color: Colors.grey1, mb: 2 }}>
              Configure and create the template that will be automatically assigned after an Initial Examination.
            </Typography>
            <Box
              sx={{
                border: `1px solid ${Colors.border}`,
                borderRadius: "12px",
                background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                p: 2.5
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Template Plan Setup
                </Typography>
                <Typography variant="caption" sx={{ color: Colors.info }}>
                  {numberOfWeeks * visitsPerWeek} visits total
                </Typography>
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                  Number of Weeks
                </Typography>
                <NumberTextField
                  value={numberOfWeeks}
                  onChange={(value) => setNumberOfWeeks(Math.max(1, value))}
                  sx={{ minWidth: "160px" }}
                />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                  Visits Per Week
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {weekVisitOptions.map((num) => (
                    <Box
                      key={num}
                      onClick={() => setVisitsPerWeek(num)}
                      sx={{
                        minWidth: "36px",
                        textAlign: "center",
                        py: 0.5,
                        px: 1.25,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: visitsPerWeek === num ? Colors.primary : "rgba(255, 255, 255, 0.05)",
                        color: visitsPerWeek === num ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                        border: "1px solid " + (visitsPerWeek === num ? Colors.primary : Colors.border),
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: visitsPerWeek === num ? Colors.primary : "rgba(255, 255, 255, 0.08)",
                          borderColor: visitsPerWeek === num ? Colors.primary : Colors.border
                        }
                      }}
                    >
                      {num}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                  Gap Between Appointments (Days)
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {gapOptions.map((num) => (
                    <Box
                      key={num}
                      onClick={() => setGap(num)}
                      sx={{
                        minWidth: "36px",
                        textAlign: "center",
                        py: 0.5,
                        px: 1.25,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: gap === num ? Colors.primary : "rgba(255, 255, 255, 0.05)",
                        color: gap === num ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                        border: "1px solid " + (gap === num ? Colors.primary : Colors.border),
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: gap === num ? Colors.primary : "rgba(255, 255, 255, 0.08)",
                          borderColor: gap === num ? Colors.primary : Colors.border
                        }
                      }}
                    >
                      {num}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2.5 }}>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                    First Visit
                  </Typography>
                  <Select
                    value={firstVisit?.appointmentTypeId || ""}
                    onChange={(e) => {
                      const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === e.target.value);
                      setFirstVisit(selectedType || null);
                    }}
                    size="small"
                    fullWidth
                    disabled={loadingAppointmentTypes}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "6px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border }
                    }}
                  >
                    {filterAppointmentTypesForPicker(appointmentTypes, firstVisit?.appointmentTypeId, user?.organizationId).map((type) => (
                      <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                    Default Visit Type
                  </Typography>
                  <Select
                    value={mostVisits?.appointmentTypeId || ""}
                    onChange={(e) => {
                      const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === e.target.value);
                      setMostVisits(selectedType || null);
                    }}
                    size="small"
                    fullWidth
                    disabled={loadingAppointmentTypes}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "6px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border }
                    }}
                  >
                    {filterAppointmentTypesForPicker(appointmentTypes, mostVisits?.appointmentTypeId, user?.organizationId).map((type) => (
                      <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Stack>
              <Box
                sx={{
                  mb: 2
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Plan of Care Preview
                </Typography>
                <Stack spacing={1}>
                  {previewPlanRows.map((week) => (
                    <Box key={week.weekNumber} sx={{ p: 1.25, borderRadius: "8px", backgroundColor: "rgba(255, 255, 255, 0.05)", border: `1px solid ${Colors.border}` }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Week {week.weekNumber}
                      </Typography>
                      {week.visits.map((visit) => (
                        <Typography key={visit.visitNumber} variant="caption" sx={{ display: "block", color: Colors.grey1 }}>
                          Visit #{visit.visitNumber}: {visit.appointmentTypeName}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              </Box>
            </Box>
          </Box>
        }
      >
        <Checkbox
          checked={autoAssignPOCAfterIE}
          onChange={() => setAutoAssignPOCAfterIE(!autoAssignPOCAfterIE)}
        />
      </SettingRow>
      <SettingRow label="Automatically send a booking link to patients during an Initial Examination" description="Recommended">
        <Checkbox
          checked={autoSendPOCLinkAfterIE}
          onChange={() => setAutoSendPOCLinkAfterIE(!autoSendPOCLinkAfterIE)}
        />
      </SettingRow>
      <Box mt={6} mb={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryButton onClick={handleSave} disabled={isLoading || loadingTemplateDetails}>
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Save Changes"
          )}
        </PrimaryButton>
      </Box>
    </FunctionalModulesLayout>
  );
}
